import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { ArrowLeft, ArrowUpRight, Check, Moon, Sun } from "lucide-react";

interface AuthLayoutProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  // Optional back link rendered above the title (e.g. "Back to sign in")
  back?: { to: string; label: string };
}

// Shared frame for every sign-in/sign-up screen: form column on the left, product story on the right
export const AuthLayout = ({ title, description, children, back }: AuthLayoutProps) => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas text-foreground">
      {/* Mirrors joylo.ai's header (.nav.wrap in joylo-web's studio.css) so the logo sits at the same size and position */}
      <header className="mx-auto flex h-[75px] w-[calc(100%-36px)] max-w-[1280px] items-center justify-between border-b border-line min-[751px]:h-[92px] min-[751px]:w-[calc(100%-64px)] min-[1051px]:w-[calc(100%-112px)]">
        <a href="/" aria-label="Joylo home">
          <img src="/logo.svg" alt="Joylo" className="h-auto w-[87px] dark:hidden min-[751px]:w-[100px]" />
          <img src="/logo-dark.svg" alt="Joylo" className="hidden h-auto w-[87px] dark:block min-[751px]:w-[100px]" />
        </a>
        <button
          type="button"
          onClick={() => setTheme(isDark ? "light" : "dark")}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground"
        >
          {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>
      </header>

      <main className="mx-auto grid w-full max-w-[1500px] flex-1 grid-cols-1 gap-[7%] px-[5%] py-8 lg:grid-cols-2 lg:py-10">
        <section className="flex items-center justify-center py-4 lg:py-8">
          <div className="w-full max-w-[405px]">
            {back && (
              <Link to={back.to} className="mb-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> {back.label}
              </Link>
            )}
            <h1 className="font-display text-[32px] font-semibold leading-[1.1] tracking-[-0.04em] text-foreground sm:text-[38px]">
              {title}
            </h1>
            {description && <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{description}</p>}
            <div className="mt-8">{children}</div>
          </div>
        </section>

        <aside className="hidden flex-col justify-center overflow-hidden rounded-[22px] border border-[#ebdfe9] bg-[linear-gradient(145deg,#f8e9e7,#f1eaf7)] px-9 pb-6 pt-12 dark:border-line dark:bg-[linear-gradient(145deg,#1a1830,#16172b)] lg:flex">
          <div className="mx-auto w-full max-w-[370px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#895574] dark:text-plum">
              From an idea to something real
            </span>
            <h2 className="my-5 font-display text-[46px] font-semibold leading-[1.08] tracking-[-0.05em] text-[#30263c] dark:text-foreground">
              What will you
              <br />
              build next?
            </h2>
            <p className="max-w-[290px] text-[14px] leading-[1.8] text-[#7b6e82] dark:text-muted-foreground">
              A client portal. A better booking experience. The business tool you wish you had.
            </p>
          </div>

          <div aria-hidden="true" className="mx-auto mb-5 mt-10 w-full max-w-[460px] -rotate-2 overflow-hidden rounded-[14px] border border-[#d9d2ce] bg-[#fffdf9] shadow-[0_15px_35px_#4b2a3514]">
            <div className="relative flex h-[31px] items-center gap-1 border-b border-[#eae4dc] bg-[#f9f6f1] px-3">
              <span className="h-[5px] w-[5px] rounded-full bg-[#d8d1c7]" />
              <span className="h-[5px] w-[5px] rounded-full bg-[#d8d1c7]" />
              <span className="h-[5px] w-[5px] rounded-full bg-[#d8d1c7]" />
              <small className="absolute left-[45%] text-[9px] text-[#847d73]">forma.studio</small>
            </div>
            <div className="grid min-h-[270px] grid-cols-[45px_1fr]">
              <div className="flex flex-col items-center gap-[15px] border-r border-[#eae3d8] bg-[#f4efe5] px-2.5 py-4">
                <strong className="mb-2.5 font-serif text-2xl text-[#3e3b30]">f.</strong>
                <span className="h-[5px] w-[19px] rounded-sm bg-[#9eaa94]" />
                <span className="h-[5px] w-[19px] rounded-sm bg-[#d4cdc0]" />
                <span className="h-[5px] w-[19px] rounded-sm bg-[#d4cdc0]" />
                <span className="h-[5px] w-[19px] rounded-sm bg-[#d4cdc0]" />
              </div>
              <div className="px-6 py-6">
                <p className="text-[8px] tracking-[0.12em] text-[#918879]">YOUR CLIENT SPACE</p>
                <h3 className="mt-2.5 font-serif text-[27px] font-normal leading-[1.2] tracking-[-0.025em] text-[#3f3d31]">
                  A little more
                  <br />
                  room to create.
                </h3>
                <div className="relative mt-4 h-[124px] overflow-hidden rounded-md bg-[#ddd0b9]">
                  <div className="absolute left-[13%] top-[18%] h-[35%] w-[20%] border-[5px] border-[#6b5a44] bg-[#e7dcc6]" />
                  <div className="absolute right-[18%] top-0 h-[70%] w-[28%] border-x-4 border-[#b9a98d] bg-[#c9d3be]" />
                  <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-[#cdbd9f]" />
                  <div className="absolute bottom-[12%] left-[12%] h-[22%] w-[40%] rounded-t-md bg-[#efe6d6]" />
                  <div className="absolute bottom-[8%] right-[20%] h-[18%] w-[14%] rounded-t-full border-4 border-b-0 border-[#6b4f3a]" />
                </div>
                <div className="mt-3.5 flex items-center justify-between">
                  <div>
                    <span className="block text-[8px] tracking-[0.1em] text-[#918879]">PROJECT STATUS</span>
                    <strong className="text-[11px] font-medium text-[#3f3d31]">Ready for your review</strong>
                  </div>
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#e5ebdf] text-[#5f7355]">
                    <Check className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 text-[12px] text-[#7b6e82] dark:text-muted-foreground">
            <span className="h-px w-6 bg-current opacity-50" />
            Build with AI. Make it your own.
          </div>
        </aside>
      </main>

      <footer className="flex items-center justify-between px-[5%] py-6 text-[12px] text-muted-foreground">
        <span>© Joylo AI</span>
        <a href="/help" className="inline-flex items-center gap-1 hover:text-foreground">
          Help center <ArrowUpRight className="h-3 w-3" />
        </a>
      </footer>
    </div>
  );
};

// Shared field/button looks for auth forms
export const authInputClass =
  "h-11 rounded-[10px] border border-input bg-surface px-3.5 text-[14px] text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0";

export const authSubmitClass =
  "flex h-11 w-full items-center justify-center gap-2 rounded-[10px] bg-gradient-sunset text-[14px] font-semibold text-white shadow-[0_3px_10px_#a23c9112] transition-shadow hover:shadow-[0_4px_14px_#a23c9133] disabled:cursor-not-allowed disabled:opacity-50";

export const authLinkClass = "font-semibold text-plum hover:underline";
